import React, { useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Button,
	IconButton
} from "@mui/material";
import { Translate } from "orion-components/i18n";
import CloseIcon from "@mui/icons-material/Close";
import { duressService } from "client-app-core";
import PropTypes from "prop-types";

const styles = {
	title: {
		color: "#fff",
		letterSpacing: "unset",
		lineHeight: "unset"
	},
	paperPropStyles: {
		backgroundColor: "#212327",
		padding: "15px",
		alignItems: "center",
		width: "600px"
	},
	inputStyles: {
		border: "1px solid #FFFFFF66",
		borderRadius: "4px",
		color: "#fff",
		backgroundColor: "#212327",
		fontSize: "13px",
		height: "32px",
		width: "265px",
		outline: "none",
		padding: "4px"
	},
	closeButton: {
		color: "#fff",
		position: "absolute",
		right: 8,
		top: 8
	},
	inputContainer: {
		display: "flex",
		gap: "10px",
		marginTop: "16px"
	},
	description: {
		color: "#fff",
		opacity: 0.7,
		textAlign: "center"
	},
	successMessage: {
		color: "#61BE49",
		marginBottom: "110px"
	},
	errorMessage: {
		color: "#D3615C",
		marginBottom: "110px"
	},
	resetButton: {
		height: 45,
		color: "#fff"
	}
};

const stopPropagation = e => {
	e.stopPropagation();
};

const DuressExitModal = ({ open, handleClose }) => {
	const activatedByWorkstationName = useSelector(state => state.duressStatus?.activatedByWorkstationName);
	const activatedByUserName = useSelector(state => state.duressStatus?.activatedByUserName);
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState(false);

	const handleDuressResetWithValues = useCallback(() => {
		duressService.deactivateDuressAlarm((err, response) => {
			if (err || response.success === false) {
				console.error("Error resetting duress alarm:", err);
				setError(true);
			} else {
				setSuccess(true);
			}
		});
	}, []);

	const handleCloseClicked = useCallback(() => {
		handleClose();
		// This timeout is to ensure the modal closes before resetting the state
		setTimeout(() => {
			setSuccess(false);
			setError(false);
		}, 300);
	}, [handleClose]);

	return (
		<Dialog
			PaperProps={{
				sx: styles.paperPropStyles
			}}
			open={open}
			disableEnforceFocus={true}
			scroll="paper"
			onMouseDown={stopPropagation}
			onTouchStart={stopPropagation}
			aria-labelledby="duress-exit-modal-title"
			aria-describedby="duress-exit-modal-description"
			data-testid="settings-app-src-shared-components-modals-duress-exit-modal-dialog"
		>
			<IconButton
				sx={styles.closeButton}
				onClick={handleCloseClicked}
				aria-label="Close modal"
				data-testid="settings-app-src-shared-components-modals-duress-exit-modal-icon-button"
			>
				<CloseIcon />
			</IconButton>

			<DialogTitle
				id="duress-exit-modal-title"
				sx={styles.title}
				disableTypography={true}
				data-testid="settings-app-src-shared-components-modals-duress-exit-modal-dialog-title"
			>
				<h3>
					<Translate value={"modals.duressExitModal.title"} />
				</h3>
			</DialogTitle>

			{success && (
				<DialogContentText variant="body2">
					<p style={styles.successMessage}>
						<Translate value={"modals.duressExitModal.success"} />
					</p>
				</DialogContentText>
			)}

			{error && (
				<DialogContentText variant="body2">
					<p style={styles.errorMessage}>
						<Translate value={"modals.duressExitModal.error"} />
					</p>
				</DialogContentText>
			)}

			{!success && !error && (
				<>
					<DialogContent data-testid="settings-app-src-shared-components-modals-duress-exit-modal-dialog-content">
						<DialogContentText id="duress-exit-modal-description" variant="body2" color="#fff">
							<p style={styles.description}>
								<Translate
									value={"modals.duressExitModal.desc"}
									primaryValue={activatedByWorkstationName}
									secondaryValue={activatedByUserName}
								/>
							</p>
						</DialogContentText>
					</DialogContent>
					<DialogActions data-testid="settings-app-src-shared-components-modals-duress-exit-modal-dialog-actions">
						<Button
							style={styles.resetButton}
							onClick={handleDuressResetWithValues}
							variant="contained"
							color="primary"
							data-testid="settings-app-src-shared-components-modals-duress-exit-modal-button"
						>
							<Translate value="modals.duressExitModal.resetDuress" />
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
};

DuressExitModal.propTypes = {
	open: PropTypes.bool.isRequired,
	handleClose: PropTypes.func.isRequired
};

export default DuressExitModal;
